import { SyntheticEvent, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from 'app/hooks';
import { FormMsg, NoFormMsg, doFormMsg } from 'components/forms/FormMessage';
import { ModalDialog } from 'components/modal/Modal';
import { IPage } from 'features/page/pageAPI';
import { patchPageAsync, postPageAsync } from 'features/page/pageSlice';
import type { IUser } from 'features/user/userAPI';
import { useUsers } from 'selectors/usersSelectors';

type PageFormProps = {
  page?: IPage;
};

type PageFormData = IPage & {
  author?: IUser;
};

export default function PageForm(props: PageFormProps) {
  const { page } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<PageFormData>({
    locale: 'nl',
    sections: [],
  } as PageFormData);
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const { users } = useUsers();

  const authorIdValue = data.author?.id ?? '';

  useEffect(() => {
    if (page) {
      setData({
        ...page,
        author: page.author,
      } as PageFormData);
    }
  }, [page]);

  const handleChangeData = (e: SyntheticEvent) => {
    doFormMsg(NoFormMsg, setFormMsg);
    const target = e.target as HTMLInputElement;
    setData({ ...data, [target.name]: target.value });
  };

  const handleChangeAuthor = (e: SyntheticEvent) => {
    doFormMsg(NoFormMsg, setFormMsg);
    const target = e.target as HTMLSelectElement;
    const selectedAuthor = users.find(
      (user) => user.id === Number(target.value),
    );
    setData({
      ...data,
      author: selectedAuthor,
    });
  };

  async function handleSubmit(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!page) {
          console.log('data:', data);
          // creating new page
          dispatch(
            postPageAsync({
              data: { ...data } as IPage,
            }),
          ).then(async (response: { payload: any }) => {
            switch (response.payload?.response.status) {
              case 200:
              case 201:
                doFormMsg(
                  {
                    message: intl.formatMessage({
                      id: 'X.SUCCESS',
                    }),
                    success: true,
                    timeout: 5000,
                  },
                  setFormMsg,
                );
                resolve(true);
                break;
              case 500:
              default:
                doFormMsg(
                  {
                    message: intl.formatMessage({ id: 'X.FAILED' }),
                    success: false,
                  },
                  setFormMsg,
                );
                reject(intl.formatMessage({ id: 'X.FAILED' }));
                break;
            }
          });
        } else {
          // updating existing page
          dispatch(patchPageAsync({ data })).then(
            async (response: { payload: any }) => {
              switch (response.payload?.response.status) {
                case 200:
                  doFormMsg(
                    {
                      message: intl.formatMessage({
                        id: 'X.SUCCESS',
                      }),
                      success: true,
                      timeout: 5000,
                    },
                    setFormMsg,
                  );
                  resolve(true);
                  break;
                case 500:
                default:
                  doFormMsg(
                    {
                      message: intl.formatMessage({ id: 'X.FAILED' }),
                      success: false,
                    },
                    setFormMsg,
                  );
                  reject(intl.formatMessage({ id: 'X.FAILED' }));
                  break;
              }
            },
          );
        }
      } catch (err) {
        doFormMsg(
          {
            message: intl.formatMessage({ id: 'X.FAILED' }),
            success: false,
          },
          setFormMsg,
        );
        reject(intl.formatMessage({ id: 'X.FAILED' }));
      }
    });
  }

  return (
    <ModalDialog
      title={<FormattedMessage id={page ? 'PAGES.EDIT' : 'PAGES.FORM.NEW'} />}
      okBtn={{
        label: intl.formatMessage({ id: 'X.SAVE' }),
        onClick: handleSubmit,
      }}
      cancelBtn={{}}
      editing={true}
      enableEscape
      formMsg={formMsg}
    >
      <Form.Group as={Row}>
        <Col sm={12} md={12} lg={12}>
          <Form.Label htmlFor="PageTitleInput">
            <FormattedMessage
              id="PAGES.FORM.TITLE_LABEL"
              defaultMessage="Title"
            />
          </Form.Label>
          <Form.Control
            id="PageTitleInput"
            type="text"
            name="title"
            value={data.title || ''}
            onChange={handleChangeData}
            placeholder={intl.formatMessage({ id: 'PAGES.FORM.TITLE_PLCH' })}
            required
            minLength={3}
          />
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Col sm={12} md={12} lg={12}>
          <Form.Label htmlFor="PageSlugInput">
            <FormattedMessage
              id="PAGES.FORM.SLUG_LABEL"
              defaultMessage="Slug"
            />
          </Form.Label>
          <Form.Control
            id="PageSlugInput"
            type="text"
            name="slug"
            value={data.slug || ''}
            onChange={handleChangeData}
            placeholder={intl.formatMessage({ id: 'PAGES.FORM.SLUG_PLCH' })}
            required
            minLength={3}
          />
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Col sm={12} md={12} lg={12}>
          <Form.Label htmlFor="PageLocaleInput">
            <FormattedMessage id="GENERAL.LOCALE.LABEL" />
          </Form.Label>
          <Form.Select
            id="PageLocaleInput"
            name="locale"
            value={data.locale || ''}
            onChange={handleChangeData}
            required
          >
            <option value="nl">
              {intl.formatMessage({ id: 'GENERAL.LOCALE.NL' })}
            </option>
            <option value="en">
              {intl.formatMessage({ id: 'GENERAL.LOCALE.EN' })}
            </option>
          </Form.Select>
        </Col>
      </Form.Group>
      <Form.Group as={Row}>
        <Col sm={12} md={12} lg={12}>
          <Form.Label htmlFor="PageAuthorInput">
            <FormattedMessage id="GENERAL.AUTHOR" />
          </Form.Label>
          <Form.Select
            id="PageAuthorInput"
            name="authorId"
            value={authorIdValue}
            onChange={handleChangeAuthor}
            required
          >
            <option value="" disabled>
              {intl.formatMessage({ id: 'GENERAL.AUTHOR' })}
            </option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Form.Group>
    </ModalDialog>
  );
}
